class RequiredWarrantyModal extends HTMLElement {
  constructor() {
    super()
    this.modal = this.querySelector('.required-warranty-modal')
    this.modalContentFront = this.querySelector('.front-modal')
    this.modalContentBack = this.querySelector('.back-modal')
    this.modalContent = this.querySelector('.required-warranty-modal-content')
    this.closeSpanX = this.querySelector('.required-warranty-modal__close-btn')
    this.closeModalBtn = this.querySelector('.required-warranty-modal-overlay')
    this.previousFocusedElement = null
    this._trapKeydownHandler = this._trapKeydownHandler.bind(this)
    this.load()
  }

  load() {
    this.addListeners()
  }

  addListeners() {
    this.closeSpanX.addEventListener('click', () => this.closeModal())
    this.closeModalBtn.addEventListener('click', () => this.closeModal())

    window.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.modal.style.display === 'flex') {
        this.closeModal()
      }
    })

    window.addEventListener('click', (event) => {
      if (event.target === this.modal) {
        this.closeModal()
      }
    })
  }

  showModal() {
    this.setAttribute('aria-expanded', 'true')
    this.previousFocusedElement = document.activeElement
    this.modalContentFront.style.display = 'none'
    this.modal.style.display = 'flex'
    this.modalContentBack.style.display = 'flex'
    document.body.classList.add('overflow-hidden')
    this.trapFocus()
  }

  closeModal() {
    this.setAttribute('aria-expanded', 'false')
    this.modal.style.display = 'none'
    document.body.classList.remove('overflow-hidden')
    this.modal.removeEventListener('keydown', this._trapKeydownHandler)

    if (this.previousFocusedElement) {
      this.previousFocusedElement.focus()
    }
  }

  isElementVisible(element) {
    if (!element || element.hasAttribute('disabled')) return false
    if (element.getAttribute('aria-disabled') === 'true') return false
    if (element.classList.contains('hidden')) return false
    if (element.closest('.hidden')) return false

    let node = element
    while (node && node !== this.modal) {
      const style = window.getComputedStyle(node)
      if (style.display === 'none' || style.visibility === 'hidden') {
        return false
      }
      node = node.parentElement
    }

    return true
  }

  isElementFocused(element) {
    const active = document.activeElement
    if (!active || !element) return false
    if (active === element || element.contains(active)) return true
    if (element.shadowRoot?.contains(active)) return true
    return active.getRootNode()?.host === element
  }

  getFocusableElements() {
    const container = this.modalContent || this.modal
    const candidates = container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), sl-icon-button:not([disabled]), [tabindex]:not([tabindex="-1"])',
    )

    return [...candidates].filter((element) => this.isElementVisible(element))
  }

  _trapKeydownHandler(event) {
    if (event.key !== 'Tab') return

    const focusableElements = this.getFocusableElements()
    if (!focusableElements.length) {
      event.preventDefault()
      return
    }

    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]
    const activeIsFocusable = focusableElements.some((element) =>
      this.isElementFocused(element),
    )

    // Focus can remain on a control that just became disabled (e.g. warranty Add).
    // That element is no longer in the focusable list, so wrap from here instead of
    // letting Tab escape into the page behind the overlay.
    if (!activeIsFocusable) {
      event.preventDefault()
      if (event.shiftKey) lastElement.focus()
      else firstElement.focus()
      return
    }

    if (event.shiftKey && this.isElementFocused(firstElement)) {
      lastElement.focus()
      event.preventDefault()
    } else if (!event.shiftKey && this.isElementFocused(lastElement)) {
      firstElement.focus()
      event.preventDefault()
    }
  }

  /**
   * Move focus off a control that is about to leave the tab order (disabled),
   * keeping keyboard users inside the dialog.
   */
  moveFocusFromElement(element) {
    if (!element || !this.isElementFocused(element)) return

    const focusableElements = this.getFocusableElements().filter((el) => el !== element)
    if (!focusableElements.length) {
      element.blur()
      return
    }

    const following = focusableElements.find(
      (el) => Boolean(element.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING),
    )
    ;(following || focusableElements[0]).focus()
  }

  trapFocus() {
    this.modal.removeEventListener('keydown', this._trapKeydownHandler)

    const focusableElements = this.getFocusableElements()
    if (focusableElements.length > 0) {
      focusableElements[0].focus()
    }

    this.modal.addEventListener('keydown', this._trapKeydownHandler)
  }
}

customElements.define('modal-required-warranty-root', RequiredWarrantyModal)
